import React, { useContext, useEffect, useState } from 'react';
import { SkeletonProvider } from '@ui/components';
import {
  useSelfService,
  LicenseUsageTableData,
  LicenseUsageChartData,
  LicenseActiveUsersTableData,
} from '../../services/selfService';

const Context = React.createContext<ContextValue>({ timeSpan: 0, setTimeSpan: () => undefined });

export const UsageReportProvider: React.FC<Props> = ({ licenseId, ...rest }) => {
  const [timeSpan, setTimeSpan] = useState<number>(180);
  const reportData = useReportData(licenseId, timeSpan);
  const enableSkeleton = !(reportData.activeUsers && reportData.chart && reportData.timeSpan);

  return (
    <Context.Provider value={{ ...reportData, setTimeSpan }}>
      <SkeletonProvider enabled={enableSkeleton} {...rest} />
    </Context.Provider>
  );
};

const useReportData = (licenseId: string, timeSpan: number) => {
  const client = useSelfService();
  const [reportData, setReportData] = useState<ReportData>({ timeSpan });

  useEffect(() => {
    setReportData(() => ({ timeSpan }));

    const ac = new AbortController();
    const chart = client.getLicenseUsageChartData(licenseId, timeSpan);
    const table = client.getLicenseUsageTableData(licenseId, timeSpan);
    const activeUsers = client.getLicenseActiveUsersTableData(licenseId);

    void Promise.all([chart, table, activeUsers]).then(([chart, table, activeUsers]) => {
      !ac.signal.aborted && setReportData({ chart, table, activeUsers, timeSpan });
    });

    return () => ac.abort();
  }, [client, licenseId, timeSpan]);

  return reportData;
};

export const useReport = () => useContext(Context);

type Props = { licenseId: string };
type ReportData = {
  timeSpan: number;
  chart?: LicenseUsageChartData;
  table?: LicenseUsageTableData;
  activeUsers?: LicenseActiveUsersTableData;
};

type ContextValue = ReportData & {
  setTimeSpan: React.Dispatch<React.SetStateAction<number>>;
};
